/**
 * `copy` — every visitor-facing string. Empty = the widget's built-in copy,
 * which each field shows as its placeholder (from the widget's own
 * defaultCopy.ts, so the two cannot drift).
 *
 * Not shown, though stored values are kept:
 *   - `cta_capture`: the widget never reads it (its capture buttons are fixed).
 *   - `sent_confirmation_matched`: the lead route never answers "matched", so
 *     every visitor sees the unmatched confirmation.
 *   - `cta_app`: the app button is edited on the App handoff tab; this
 *     section shows its state read-only.
 */
import {
  DEFAULT_AUDIO_NOTICE,
  DEFAULT_HEADLINE,
  DEFAULT_SUBHEADLINE,
  defaultChatNotice,
  defaultLiveNotice,
  defaultLiveNoticeShort,
  defaultNoticeAtCollection,
  defaultOutOfScopeMessage,
  defaultRouteCtaLabel,
  defaultSentConfirmation,
} from '../../../../diagnosis-widget/src/defaultCopy';
import { useSession } from '../../auth/session';
import { Field } from '../../ui/kit';
import { useDraft } from '../draft';
import { AppHandoffStatus, FORMATTING_HINT, NullableText, SectionCard, SettingsTabLink, usePath } from './common';

/** The languages the widget ships copy for. A stored value outside the list is still shown. */
const LANGUAGES = [{ value: 'en-US', label: 'English (US)' }];

export function CopySection() {
  const { org } = useSession();
  const { edited } = useDraft();
  const locale = usePath<string>('copy.locale');
  const partner = org?.display_name ?? null;
  const mode = edited.routing.mode;
  const routeKind = mode === 'partner_call' ? 'call' : mode === 'partner_dispatch' || mode === 'partner_booking' ? 'form' : null;
  const languages = LANGUAGES.some((l) => l.value === locale.value) ? LANGUAGES : [...LANGUAGES, { value: locale.value, label: locale.value }];
  return (
    <SectionCard id="sec-copy" title="Copy" description="Words the visitor reads. Leave a field empty to keep the default shown in it.">
      <NullableText path="copy.headline" label="Headline" placeholder={DEFAULT_HEADLINE} />
      <NullableText path="copy.subheadline" label="Subtitle" placeholder={DEFAULT_SUBHEADLINE} hint="The line under the headline." />
      <NullableText
        path="copy.cta_route"
        label="Send-to-team button"
        placeholder={defaultRouteCtaLabel(routeKind ?? 'form', partner)}
        hint={
          routeKind ? (
            'Name your team, e.g. “Send to Cascade maintenance”.'
          ) : (
            <>
              Not shown right now: visitors don’t get a send-to-team button (<SettingsTabLink tab="routing">After diagnosis</SettingsTabLink>).
            </>
          )
        }
      />
      <AppHandoffStatus />
      <NullableText path="copy.disclaimer_md" label="Disclaimer" multiline placeholder="None" hint={`Shown under the diagnosis. ${FORMATTING_HINT}`} />
      <NullableText
        path="copy.out_of_scope_message"
        label="Out-of-scope message"
        multiline
        placeholder={defaultOutOfScopeMessage(partner)}
        hint="Shown when a problem is outside the trades you cover."
      />
      <NullableText
        path="copy.sent_confirmation_unmatched"
        label="Sent confirmation"
        multiline
        placeholder={defaultSentConfirmation(partner, false)}
        hint="Shown under “Sent” after a visitor sends you their request."
      />
      <NullableText
        path="copy.notice_at_collection_md"
        label="Notice at collection"
        multiline
        placeholder={defaultNoticeAtCollection(partner ?? 'the team', false)}
        hint={`Shown under the contact form. Include a link to your privacy notice. ${FORMATTING_HINT}`}
      />
      <div className="grid-2">
        <NullableText path="copy.audio_notice" label="Sound recording notice" multiline placeholder={DEFAULT_AUDIO_NOTICE} hint="Before a visitor records audio." />
        <NullableText
          path="copy.live_notice"
          label="Live conversation notice (full)"
          multiline
          placeholder={defaultLiveNotice(edited.scope.live.camera)}
          hint="Shown when a visitor taps “More” under the short line."
        />
      </div>
      <div className="grid-2">
        <NullableText
          path="copy.live_notice_short"
          label="Live conversation notice (short line)"
          placeholder={defaultLiveNoticeShort({ voice: edited.scope.modes.live, chat: edited.scope.modes.chat })}
          hint="Always shown above Talk live and Chat. It must say the visitor is talking with an AI, not a person, and that it listens."
        />
        <NullableText path="copy.chat_notice" label="Chat notice" multiline placeholder={defaultChatNotice()} hint="The AI notice for Chat." />
      </div>
      {languages.length === 1 ? (
        // One language: a select with one option reads as broken. Plain text
        // until the widget ships another locale (second audit, 2026-09-27).
        <div className="field" role="group" aria-labelledby="copy-language-label">
          <span className="field-title" id="copy-language-label">
            Language
          </span>
          <p style={{ margin: 0 }} data-testid="copy-language">
            {languages[0]!.label}
          </p>
          <p className="hint">The language of the widget’s built-in text. More will be listed here as they become available.</p>
        </div>
      ) : (
        <Field label="Language" hint="The language of the widget’s built-in text." issues={locale.issues}>
          {(p) => (
            <select {...p} className="input" style={{ maxWidth: 220 }} value={locale.value} onChange={(e) => locale.set(e.target.value)}>
              {languages.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          )}
        </Field>
      )}
    </SectionCard>
  );
}
